import { createAsyncThunk } from "@reduxjs/toolkit";
import { enqueueSnackbar } from "notistack";
import {
  cloudChiprApi,
  PatchUsersMeAccountsByAccountIdResourcesApiArg,
  ResourceType,
} from "../../../../services/cloudchipr.api";
import { selectedResourcesAccountIdsSelector } from "../../selectors/grid/selection/account/selectedResourcesAccountIdsSelector";
import { getResourceGridResourceProtectionFixedCacheKey } from "../../utils/helpers/fixedCacheKeys";
import { enqueueSnackbarErrorAlert } from "@/components/pages/common/snackbar-alert/EnqueueSnackbarErrorAlert";
import { RootState } from "@/store/store";
import { updateGridResourceDataThunk } from "@/store/resources-grid/thunks/data/updateGridResourceDataThunk";
import { selectedResourceIdsByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourceIdsByMasterIdSelector";

interface GridSelectedResourcesProtectThunkArgs {
  actionType: "protect" | "unprotect";
  masterGridId: string;
  resourceType: ResourceType;
}

export const gridSelectedResourcesProtectThunk = createAsyncThunk(
  "resourcesGrid/gridSelectedResourcesProtect",
  async (
    {
      actionType,
      masterGridId,
      resourceType,
    }: GridSelectedResourcesProtectThunkArgs,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    const accountIds = selectedResourcesAccountIdsSelector(state, masterGridId);

    const selectedResourceIds =
      selectedResourceIdsByMasterIdSelector(state, masterGridId) ?? [];

    const payloadResources = selectedResourceIds.map((id) => {
      return {
        id: id,
        type: resourceType,
        is_protected: actionType === "protect",
      };
    });

    try {
      const promises = accountIds?.map((accountId) => {
        return dispatch(protectThunk({ accountId, body: payloadResources }));
      });

      await Promise.all(promises ?? []);

      dispatch(updateGridResourceDataThunk(masterGridId));

      enqueueSnackbar(
        `Protection ${actionType === "protect" ? "enabled" : "disabled"}`,
        {
          variant: "snackbarAlert",
          AlertSnackBarProps: { severity: "success" },
        },
      );
    } catch (e) {
      // @ts-expect-error todo: fix api spec;
      enqueueSnackbarErrorAlert(e?.data?.message);
    }
  },
);

const protectThunk = createAsyncThunk(
  "resourcesGrid/protectThunk",
  async (
    request: PatchUsersMeAccountsByAccountIdResourcesApiArg,
    { dispatch },
  ) => {
    const { patchUsersMeAccountsByAccountIdResources } =
      cloudChiprApi.endpoints;

    if (!request.body.length) {
      return;
    }

    const resourceIds = request.body.map((item) => item.id ?? "");

    return await dispatch(
      patchUsersMeAccountsByAccountIdResources.initiate(request, {
        fixedCacheKey: getResourceGridResourceProtectionFixedCacheKey(
          resourceIds,
          request.accountId,
        ),
      }),
    ).unwrap();
  },
);
